<template>
  <v-container fluid>
    <dialog-confirm ref="delete-confirm">
      <div class="text-subtitle-1">
        {{ $t('are_you_sure_you_want_to_delete_this') }}
      </div>
    </dialog-confirm>

    <div class="d-flex justify-space-between align-center">
      <h2>{{ $tc('banner', 2) }}</h2>
      <v-btn
        v-if="userLoaded && user.can('create_banner')"
        outlined
        color="primary"
        to="/banner/create">
        <v-icon class="mr-1 ml-n2">mdi-plus</v-icon>
        {{ $t('new_banner' )}}
      </v-btn>
    </div>

    <table-pagination-controls
      :total-rows="remoteData.totalRows"
      :current-page.sync="remoteData.currentPage"
      :rows-per-page.sync="remoteData.rowsPerPage"
      @update:currentPage="refreshResults"
      @update:rowsPerPage="initiateSearch"
      :per-page-options="remoteData.pageOptions"/>

    <!-- Data Table -->
    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="vf-table mt-2"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()">

        <template #item.customer="row">
          <if-set :value="row.item.customer.name" dash>
            {{ row.item.customer.name }}
          </if-set>
        </template>

        <template #item.type="row">
          <if-set :value="row.item.type" dash>
            {{ $t(row.item.type) }}
          </if-set>
        </template>

        <template #item.visible="row">
          <span v-text="row.item.visible ? $t('yes') : $t('no')"/>
        </template>

        <template #item.expiration="row">
          <if-set :value="row.value" dash>
            <multi-time-display :time="row.value"/>
          </if-set>
        </template>

        <template #item.created="row">
          <if-set :value="row.item.created" dash>
            <multi-time-display :time="row.item.created"/>
          </if-set>
        </template>

        <template #item.updates="row">
          <if-set :value="row.item.updated" dash>
            <multi-time-display :time="row.item.updated"/>
          </if-set>
        </template>

        <template #item.actions="row">
          <vf-tooltip :text="$t('edit')">
            <v-btn :to="'/banner/update/' + row.item.id" color="primary" fab text x-small>
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </vf-tooltip>
          <vf-tooltip :text="$t('delete')">
            <v-btn @click="deleteBanner(row.item)" color="primary" fab text x-small>
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </vf-tooltip>
        </template>
      </v-data-table>
    </div>
  </v-container>

</template>

<script>
import VfTooltip from '@/components/shared/VfTooltip'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import DialogConfirm from '@/components/shared/DialogConfirm'
import IfSet from '@/components/shared/IfSet'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'

export default {
  name: 'BannerIndex',
  components: {
    VfTooltip,
    MultiTimeDisplay,
    TablePaginationControls,
    DialogConfirm,
    IfSet
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin,
    BrowserUtilitiesMixin
  ],
  data () {
    return {
      model: 'Banner',
      searchCriteria: {
        name: ''
      },
      tableFields: [
        {
          value: 'customer',
          text: this.$tc('customer'),
          sortable: true
        },
        {
          value: 'type',
          text: this.$t('type'),
          sortable: true
        },
        {
          value: 'style',
          text: this.$t('style'),
          sortable: true
        },
        {
          value: 'visible',
          text: this.$t('visible'),
          sortable: true
        },
        {
          value: 'expiration',
          text: this.$t('expiration'),
          sortable: true
        },
        {
          value: 'created',
          text: this.$t('created'),
          sortable: true
        },
        {
          value: 'updated',
          text: this.$t('updated'),
          sortable: true
        },
        {
          value: 'actions',
          text: this.$tc('action', 2)
        }
      ]
    }
  },
  methods: {
    buildSearchCriteria() {
      const searchCriteria = {
        expand: 'customer',
        fields: 'id,customer.name,type,style,visible,expiration,created,updated',
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }

      if (this.remoteData.sortBy !== null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }

      return searchCriteria
    },
    async deleteBanner(item) {
      if (await this.$refs['delete-confirm'].confirm()) {
        try {
          await item.destroy()
          await this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$i18n.tc('banner')} ${this.$i18n.t('generic_delete_success')}`
          )
          this.initiateSearch()
        } catch (error) {
          this.handleError(error)
        }
      }
    }
  },
  mounted() {
    this.updateBrowserTitle(this.$tc('banner', 2))
    this.initiateSearch()
  }
}

</script>
